import React, { useRef, useState, useEffect } from 'react';
import { keyframes } from '@emotion/react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Stack,
    IconButton,
    Divider,
    Slider,
} from '@mui/material';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import DailyBreathingIcon from "../../assets/care/DailyBreathing.png";
import JustBreathe from "../../assets/care/JustBreathe.png";
import BreatheRight from "../../assets/care/BreatheRight.png";
import ManageThoughts from "../../assets/manage-thoughts.png";

const songs = [
    {
        title: "Daily Breathing",
        time: "5:00",
        src: "/sounds/daily-breathing/Box-Breathe.aac",
        theme: "theme1",
        icon: DailyBreathingIcon,
    },
    {
        title: "Just Breathe",
        time: "5:00",
        src: "/sounds/daily-breathing/Just-Breathe.aac",
        theme: "theme2",
        icon: JustBreathe,
    },
    {
        title: "Breathe Right",
        time: "5:00",
        src: "/sounds/daily-breathing/Breathe-Right.mp3",
        theme: "theme3",
        icon: BreatheRight,
    },
];

// Animation keyframes
const theme1Sway = keyframes`
    0%, 100% { 
        transform: translateX(-10px) translateY(0px) rotate(-2deg); 
        opacity: 0.6;
    }
    50% { 
        transform: translateX(10px) translateY(-15px) rotate(2deg); 
        opacity: 0.9;
    }
`;

const theme2Drop = keyframes`
    0% { 
        transform: translateY(-100vh) translateX(0px); 
        opacity: 0;
    }
    10% { 
        opacity: 1;
    }
    90% { 
        opacity: 1;
    }
    100% { 
        transform: translateY(100vh) translateX(20px); 
        opacity: 0;
    }
`;

const theme3Float = keyframes`
    0%, 100% { 
        transform: translateY(0px) translateX(0px) rotate(0deg) scale(0.8); 
        opacity: 0.3;
    }
    25% { 
        transform: translateY(-20px) translateX(15px) rotate(90deg) scale(1.2); 
        opacity: 0.8;
    }
    50% { 
        transform: translateY(-10px) translateX(-10px) rotate(180deg) scale(1); 
        opacity: 1;
    }
    75% { 
        transform: translateY(-30px) translateX(5px) rotate(270deg) scale(1.1); 
        opacity: 0.7;
    }
`;

const pulseGlow = keyframes`
    0%, 100% { 
        box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
        transform: scale(1);
    }
    50% { 
        box-shadow: 0 0 40px rgba(255, 255, 255, 0.4);
        transform: scale(1.05);
    }
`;

const DailyBreathing = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [showSlider, setShowSlider] = useState(false);
    const [isRepeat, setIsRepeat] = useState(false);
    const [isShuffle, setIsShuffle] = useState(false);
    const [volume, setVolume] = useState(1);
    const [favorites, setFavorites] = useState([]);
    const progressRef = useRef(null);
    const currentTimeRef = useRef(null);
    const durationRef = useRef(null);


    // Animation elements state
    const [animationElements, setAnimationElements] = useState([]);

    console.log("echo soundscapes rendered")

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        audio.src = songs[currentIndex].src;
        audio.load();

        if (isPlaying) {
            audio.play().catch(() => { });
        }
    }, [currentIndex]);


    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleTimeUpdate = () => {
            if (!audio.duration) return;

            // Update progress bar width
            if (progressRef.current) {
                progressRef.current.style.width = `${(audio.currentTime / audio.duration) * 100}%`;
            }

            // Update current time display
            if (currentTimeRef.current) {
                currentTimeRef.current.textContent = formatTime(audio.currentTime);
            }

            // Update total duration
            if (durationRef.current) {
                durationRef.current.textContent = formatTime(audio.duration);
            }
        };

        const handleEnded = () => {
            if (isRepeat) {
                audio.currentTime = 0;
                audio.play();
            } else {
                playNext();
            }
        };

        audio.addEventListener("timeupdate", handleTimeUpdate);
        audio.addEventListener("ended", handleEnded);

        return () => {
            audio.removeEventListener("timeupdate", handleTimeUpdate);
            audio.removeEventListener("ended", handleEnded);
        };
    }, [isRepeat, isShuffle]);

    // Get background gradient based on current song
    const getBackgroundGradient = () => {
        if (!isPlaying) {
            return 'linear-gradient(145deg, #4fc3f7 0%, #0277bd 100%)';

        }

        const currentTheme = songs[currentIndex].theme;
        switch (currentTheme) {
            case 'theme1':
                return 'linear-gradient(145deg, #80cbc4 0%, #00695c 100%)';
            case 'theme2':
                return 'linear-gradient(145deg, #f48fb1 0%, #c2185b 100%)';
            case 'theme3':
                return 'linear-gradient(135deg, #a855f7 0%, #9333ea 25%, #7c3aed 50%, #6d28d9 75%, #5b21b6 100%)';
            default:
                return 'linear-gradient(145deg, #4fc3f7 0%, #0277bd 100%)';
        }
    };

    const playSong = (index) => {
        setCurrentIndex(index);
        setIsPlaying(true);
    };

    const togglePlayPause = () => {
        if (!audioRef.current) return;
        if (isPlaying) audioRef.current.pause();
        else audioRef.current.play();
        setIsPlaying(!isPlaying);
    };

    const playNext = () => {
        setCurrentIndex((prev) => (prev + 1) % songs.length);
    };

    const playPrev = () => {
        setCurrentIndex((prev) => (prev - 1 + songs.length) % songs.length);
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const toggleFavorite = (index) => {
        setFavorites((prev) =>
            prev.includes(index)
                ? prev.filter((i) => i !== index)
                : [...prev, index]
        );
    };

    return (
        <Box>
            <Card
                sx={{
                    height: {
                        xs: 'calc(100vh - 56px)',
                        sm: 'calc(100vh - 64px)',
                        md: 'calc(100vh - 70px)',
                    },
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    background: getBackgroundGradient(),
                    color: '#fff',
                    borderRadius: 0,
                    position: 'relative',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    transition: 'background 1s ease-in-out'
                }}
            >
                {/* Animated Background Elements */}
                {animationElements.map((element) => (
                    <Box key={element.id} sx={element.style} />
                ))}

                {/* Pulsing Glow Effect for Playing State */}
                {isPlaying && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
                            animation: `${pulseGlow} 3s ease-in-out infinite`,
                            zIndex: 0,
                            pointerEvents: 'none'
                        }}
                    />
                )}

                {/* Left Panel */}
                <Box
                    sx={{
                        width: { xs: '100%', md: '40%' },
                        position: 'relative',
                        zIndex: 1,
                        height: '100%',              // fill parent Card height
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',    // vertical center for the icon+text
                        // optional padding from top
                    }}
                >
                    {/* Header at top-left */}
                    <Box sx={{
                        position: 'absolute', top: { xs: 4, sm: 14, md: 16 }, left: { xs: 4, sm: 14, md: 16 }, zIndex: 5, ml: { xs: 2, md: 2 },
                        pt: { xs: 2, md: 0 }
                    }}>
                        <Typography
                            variant="h5"
                            fontWeight={300}
                            sx={{
                                fontSize: {
                                    xs: '1.2rem',  // mobile
                                    sm: '1.3rem',  // tablets
                                    md: '1.5rem',  // desktops
                                }
                            }}
                        >
                            {/* Nature's Echo Awaits */}
                            Breath Right with Echo
                        </Typography>
                    </Box>

                    <Box sx={{
                        position: 'absolute', top: { xs: 4, sm: 14, md: 16 }, right: { xs: 4, sm: 14, md: 16 }, zIndex: 5, ml: { xs: 2, md: 2 },
                        pt: { xs: 2, md: 0 }
                    }}>
                        <IconButton
                            onClick={() => navigate(-1)}
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                width: { xs: 24, sm: 30 },
                                height: { xs: 24, sm: 30 },
                                mr: 2,
                                borderRadius: '50%',
                                color: '#ffffff',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)'
                                }
                            }}
                        >
                            <ArrowBackIcon sx={{ fontSize: { xs: '18px', sm: '22px' } }} />
                        </IconButton>
                    </Box>


                    {/* Candle and Text */}
                    <CardContent
                        sx={{
                            p: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            width: '100%',
                            mt: { xs: 12, sm: 14, md: 0 }
                        }}
                    >
                        {/* ---- ICON + ANIMATED CIRCLES (wrapper reserves space) ---- */}
                        <Box
                            sx={{
                                width: { xs: '70%', md: '40vw' },
                                // Reserve vertical space so absolutely-positioned circles don't overlap content below
                                height: { xs: 180, md: 220 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                mb: 2, // small gap before description
                                pointerEvents: 'none', // allow clicks to pass through if needed
                            }}
                        >
                            {/* Animated Circles (absolute but inside a container with height) */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    width: { xs: '180px', sm: '200px', md: '220px' },
                                    height: { xs: '180px', sm: '200px', md: '220px' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'none',
                                }}
                            >
                                {[1, 2, 3, 4].map((index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            position: 'absolute',
                                            borderRadius: '50%',
                                            border:
                                                songs[currentIndex].theme === 'theme1'
                                                    ? '2px solid rgba(192, 224, 204, 0.35)'
                                                    : songs[currentIndex].theme === 'theme2'
                                                        ? '2px solid rgba(59, 130, 246, 0.35)'
                                                        : songs[currentIndex].theme === 'theme3'
                                                            ? '2px solid rgba(168, 85, 247, 0.35)'
                                                            : '2px solid rgba(255, 255, 255, 0.22)',
                                            width: `${index * 60}px`,   // slightly smaller steps to fit container
                                            height: `${index * 60}px`,
                                            animation: isPlaying ? `${pulseGlow} ${2 + index * 0.45}s ease-in-out infinite` : 'none',
                                            animationDelay: `${index * 0.25}s`,
                                            opacity: 0.9,
                                        }}
                                    />
                                ))}
                            </Box>

                            {/* Center Icon (on top of circles) */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 3,
                                    width: { xs: 60, sm: 70, md: 84 },
                                    height: { xs: 60, sm: 70, md: 84 },
                                    background:
                                        songs[currentIndex].theme === 'theme1'
                                            ? 'linear-gradient(135deg, rgba(34,197,94,0.32), rgba(34,197,94,0.16))'
                                            : songs[currentIndex].theme === 'theme2'
                                                ? 'linear-gradient(135deg, rgba(59,130,246,0.32), rgba(59,130,246,0.16))'
                                                : songs[currentIndex].theme === 'theme3'
                                                    ? 'linear-gradient(135deg, rgba(168,85,247,0.32), rgba(168,85,247,0.16))'
                                                    : 'linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.08))',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '2rem',
                                    backdropFilter: 'blur(6px)',
                                    pointerEvents: 'auto', // allow interactions if needed
                                }}
                            >
                                {/* {songs[currentIndex].theme === 'theme1' && '🌲'}
                                {songs[currentIndex].theme === 'theme2' && '🌧️'}
                                {songs[currentIndex].theme === 'theme3' && '✨'} */}
                                {isPlaying ? (
                                    <Box
                                        component="img"
                                        src={songs[currentIndex].icon}
                                        alt={songs[currentIndex].title}
                                        sx={{
                                            width: { xs: 30, sm: 36, md: 48 },
                                            height: { xs: 30, sm: 36, md: 48 },
                                            objectFit: 'contain',
                                            borderRadius: '50%',
                                        }}
                                    />
                                ) : (
                                    <Box
                                        component="img"
                                        src={ManageThoughts}
                                        alt="Manage Thoughts"
                                        sx={{
                                            width: { xs: 30, sm: 36, md: 48 },
                                            height: { xs: 30, sm: 36, md: 48 },
                                            objectFit: 'contain',
                                            borderRadius: '50%',
                                        }}
                                    />
                                )}

                            </Box>
                        </Box>

                        <Typography
                            variant="body1"
                            sx={{
                                opacity: 0.9,
                                maxWidth: { xs: '92%', md: '80%' },
                                mt: { xs: 4, md: 2 }, // small separation
                                fontSize: {
                                    xs: '1rem',   // mobile
                                    sm: '1.1rem',     // small tablets
                                    md: '1.2rem',   // desktops
                                },
                            }}
                        >
                            Take 3 minutes to pause and breathe.
                        </Typography>

                        {/* ---- DESCRIPTION (below icon, won't overlap) ---- */}
                        <Typography
                            variant="body1"
                            sx={{
                                opacity: 0.9,
                                maxWidth: { xs: '92%', md: '80%' },
                                mt: { xs: 2, md: 2 }, // small separation
                            }}
                        >
                            {/* {getCurrentSongDescription()} */}

                            Discover a series of immersive breathing journeys
                            designed to calm your mind, balance your emotions.
                            Each session invites you to slow down, listen,
                            and breathe with awareness.
                        </Typography>
                    </CardContent>
                </Box>

                {/* Right Panel */}
                <CardContent sx={{
                    width: { xs: '100%', md: '60%' },
                    px: { xs: 2, sm: 3, md: 0 },
                    py: { xs: 1, sm: 2, md: 0 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    mt: { xs: 0, sm: 0, md: 5 },
                    marginLeft: { xs: 1 },
                    // position: "relative",
                    zIndex: 2
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                textAlign: { xs: 'center', sm: 'left' }
                            }}
                        >
                            echo Recommendations
                        </Typography>

                        <IconButton
                            onClick={() => navigate(-1)}
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                width: { sm: 30, md: 40 },
                                height: { sm: 30, md: 40 },
                                mr: 2,
                                borderRadius: '50%',
                                color: '#ffffff',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)'
                                }
                            }}
                        >
                            <ArrowBackIcon sx={{ fontSize: { xs: '18px', sm: '22px' } }} />
                        </IconButton>
                    </Box>

                    <Stack spacing={2} sx={{ flexGrow: 1, mr: 2 }}>
                        {songs.map((song, index) => (
                            <Box
                                key={index}
                                onClick={() => playSong(index)}
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    bgcolor: index === currentIndex ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    backdropFilter: 'blur(15px)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    {/* Image */}
                                    <Box
                                        component="img"
                                        src={song.icon}
                                        alt={`${song.title}-icon`}
                                        sx={{
                                            width: { xs: 40, sm: 60, md: 80 },
                                            height: { xs: 40, sm: 60, md: 80 },
                                            borderRadius: 2,
                                            flexShrink: 0,
                                        }}
                                    />

                                    {/* Text section */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', color: '#fff' }}>
                                        {/* Title */}
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {song.title}
                                        </Typography>

                                        {/* Description (only for first 3 indexes) */}
                                        {(index === 0 || index === 1 || index === 2) && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    opacity: 0.8,
                                                    mt: 0.5,
                                                    fontSize: '0.9rem',
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {index === 0
                                                    ? 'Start your day with gentle guided breathing to restore focus and calm. This session helps regulate your breath, release overnight tension, and prepare your mind for a balanced day ahead.'
                                                    : index === 1
                                                        ? 'A mid-day reset to help you pause, slow down, and realign. Follow simple breathing rhythms that clear mental fatigue, lower stress, and improve concentration during work hours.'
                                                        : 'An evening relaxation practice to unwind and recharge. Learn mindful breathing patterns that ease the body into deep rest and improve overall emotional balance.'}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                            </Box>
                        ))}
                    </Stack>

                    {/* Music Controls - Right Aligned */}
                    <Box sx={{
                        mt: { xs: 2, md: 4 },
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        pr: { xs: 2, sm: 4 },
                        mb: 2
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '28px',
                            padding: '4px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                        }}>
                            {/* Navigation Controls */}
                            <IconButton
                                onClick={playPrev}
                                size="large"
                                sx={{
                                    color: '#fff',
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                                }}
                            >
                                <SkipPreviousIcon sx={{ fontSize: 28 }} />
                            </IconButton>

                            <IconButton
                                onClick={togglePlayPause}
                                size="large"
                                sx={{
                                    color: '#fff',
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                                }}
                            >
                                {isPlaying ? (
                                    <PauseCircleIcon sx={{ fontSize: 36 }} />
                                ) : (
                                    <PlayCircleIcon sx={{ fontSize: 36 }} />
                                )}
                            </IconButton>

                            <IconButton
                                onClick={playNext}
                                size="large"
                                sx={{
                                    color: '#fff',
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                                    mr: 1
                                }}
                            >
                                <SkipNextIcon sx={{ fontSize: 28 }} />
                            </IconButton>

                            {/* Divider */}
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                    height: 24,
                                    alignSelf: 'center'
                                }}
                            />

                            <IconButton
                                onClick={() => setIsRepeat(!isRepeat)}
                                size="large"
                                sx={{
                                    color: isRepeat ? '#FFD700' : '#fff',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        color: isRepeat ? '#FFD700' : '#fff'
                                    }
                                }}
                            >
                                <ReplayIcon sx={{ fontSize: 24 }} />
                            </IconButton>

                            <IconButton
                                onClick={() => setIsShuffle(!isShuffle)}
                                size="large"
                                sx={{
                                    color: isShuffle ? '#90EE90' : '#fff',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        color: isShuffle ? '#90EE90' : '#fff'
                                    }
                                }}
                            >
                                <ShuffleIcon sx={{ fontSize: 24 }} />
                            </IconButton>

                            <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                                {/* Volume Icon */}
                                <IconButton
                                    onClick={() => setShowSlider((prev) => !prev)}
                                    sx={{ color: volume === 0 ? "#aaa" : "#fff", mr: 1 }}
                                >
                                    <VolumeUpIcon />
                                </IconButton>

                                {/* Vertical Slider */}
                                {showSlider && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            bottom: 40, // adjust depending on layout
                                            left: "50%",
                                            transform: "translateX(-50%)",
                                            height: 120,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: "rgba(0,0,0,0.5)",
                                            borderRadius: 2,
                                            p: 1,
                                            zIndex: 10,
                                        }}
                                    >
                                        <Slider
                                            orientation="vertical"
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={volume}
                                            onChange={(e, val) => {
                                                setVolume(val);
                                                if (audioRef.current) audioRef.current.volume = val;
                                            }}
                                            sx={{
                                                height: 100,
                                                color: "#fff",
                                                "& .MuiSlider-thumb": { width: 12, height: 12 },
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>

                        </Box>
                    </Box>

                    {/* Audio Element (Hidden) */}
                    <audio
                        ref={audioRef}
                        src={songs[currentIndex].src}
                        onTimeUpdate={null}
                        onLoadedMetadata={(e) => setDuration(e.target.duration)}
                        onEnded={playNext}
                    />

                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <Typography variant="caption" sx={{ minWidth: 35 }} ref={currentTimeRef}>
                            0:00
                        </Typography>

                        <Box
                            sx={{
                                flex: 1,
                                height: 5,
                                mx: 2,
                                bgcolor: "rgba(255, 255, 255, 0.3)",   // softer background instead of #333
                                borderRadius: 5,
                                position: "relative",
                                cursor: "default",
                                overflow: "hidden",                     // ensures rounded edges work
                            }}
                        >
                            <Box
                                ref={progressRef}
                                sx={{
                                    height: "100%",
                                    width: duration ? `${(currentTime / duration) * 100}%` : "0%",
                                    background: "linear-gradient(90deg, #667eea, #764ba2)", // gradient instead of plain white
                                    transition: "width 0.1s linear",
                                }}
                            />
                        </Box>

                        <Typography variant="caption" sx={{ minWidth: 35 }} ref={durationRef}>
                            0:00
                        </Typography>
                    </Box>

                </CardContent>
            </Card>
        </Box>
    );
};

export { DailyBreathing };